import { File, Loader2, UploadCloud, X } from "lucide-react"
import Section from "../../section"
import Dropzone, { type DropzoneState } from "shadcn-dropzone"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
	requiredReferenceStatuses,
	paymentModeSchema,
	type paymentModeSchemaType,
} from "../../schemas"
import { Button } from "@/components/ui/button"
import React, { useEffect, useRef, useState } from "react"
import { useOfflinePayment } from "../hooks/use-offline"
import { toast } from "sonner"
import { format } from "date-fns"
import { usePayment } from "../../use-payment"
import { useDebounce } from "use-debounce"

const SelfOfflinePaymentForm = ({
	payment_id,
	finalAmout,
}: {
	payment_id: string
	finalAmout: number
}) => {
	const { selfOfflinePaymentForm, setSelectedPaymentPlan } = usePayment()
	const form = selfOfflinePaymentForm.form
	const [isSelectedOther, setIsSelectedOther] = useState<boolean>(false)
	const {
		useGetOfflinePaymentDetails,
		useGetFile,
		useValidateTransactionReference,
	} = useOfflinePayment()
	const { mutateAsync: fetchFile, isPending: isFilePending } = useGetFile()

	const { data } = useGetOfflinePaymentDetails({ payment_id })

	// Transaction Reference Validation
	const transactionReference = form.watch("transaction_reference")
	const [debouncedTransactionReference] = useDebounce(
		transactionReference,
		500,
	)
	const { data: isTransactionReferenceExists, isFetching: isValidating } =
		useValidateTransactionReference({
			reference_number: debouncedTransactionReference || "",
		})

	// Use ref to track previous values and prevent unnecessary re-renders
	const previousDebouncedRef = useRef<string>("")
	const previousValidationResultRef = useRef<boolean | undefined>(undefined)
	const isProcessingRef = useRef<boolean>(false)

	useEffect(() => {
		// Skip if currently processing to prevent cascading triggers
		if (isProcessingRef.current) {
			return
		}

		// Skip if debounced value hasn't actually changed
		const currentDebounced = debouncedTransactionReference || ""
		if (previousDebouncedRef.current === currentDebounced) {
			return
		}

		// If field is cleared (empty), clear errors and reset refs
		if (!currentDebounced) {
			previousDebouncedRef.current = ""
			previousValidationResultRef.current = undefined
			if (form.formState.errors.transaction_reference) {
				form.clearErrors("transaction_reference")
			}
			return
		}

		// Skip if validation is still in progress (only for non-empty values)
		if (isValidating) {
			return
		}

		// Update refs
		previousDebouncedRef.current = currentDebounced
		previousValidationResultRef.current = isTransactionReferenceExists

		// Handle validation result
		if (isTransactionReferenceExists === true) {
			// Transaction reference exists - set error
			const currentError =
				form.formState.errors.transaction_reference?.message
			if (currentError !== "Transaction Reference already exists") {
				isProcessingRef.current = true
				form.setError("transaction_reference", {
					type: "manual",
					message: "Transaction Reference already exists",
				})
				// Reset processing flag after state update
				setTimeout(() => {
					isProcessingRef.current = false
				}, 0)
			}
		} else if (isTransactionReferenceExists === false) {
			// Transaction reference is valid - clear errors and trigger validation
			isProcessingRef.current = true
			form.clearErrors("transaction_reference")
			form.trigger("transaction_reference").then(isValid => {
				// Only trigger full form validation if field is valid and no other errors exist
				if (isValid) {
					const hasOtherErrors = Object.keys(
						form.formState.errors,
					).some(key => key !== "transaction_reference")
					if (!hasOtherErrors) {
						form.trigger()
					}
				}
				isProcessingRef.current = false
			})
		}
		// If isTransactionReferenceExists is undefined, the query hasn't completed yet
		// This will be handled when the query completes
	}, [
		isTransactionReferenceExists,
		debouncedTransactionReference,
		isValidating,
	])

	useEffect(() => {
		if (data) {
			setSelectedPaymentPlan(`self-${data.additional_info.plan_id}`)
			fetchFile({
				file_url: data?.proof_url,
			})
				.then((file: File) => {
					form.reset({
						account_holder_name: data.account_holder_name,
						bank_name: data.bank_name,
						transaction_reference: data.reference_number,
						amount_paid: Number(data.amount),
						payment_date: format(
							new Date(data.paid_at),
							"yyyy-MM-dd",
						),
						remarks: data.remarks,
						payment_mode: data.mode,
						proof_of_payment: file,
					})
					if (
						!paymentModeSchema.options.includes(
							data.mode as paymentModeSchemaType,
						)
					) {
						setIsSelectedOther(true)
					}
					form.setValue("payment_mode", data.mode)
					form.setValue("final_payable_amount", finalAmout)
				})
				.catch(() => {
					toast.error(
						"Error Fetching Payment Details. Please refresh and try again",
					)
				})
		}
	}, [data])

	useEffect(() => {
		form.setValue("final_payable_amount", finalAmout)
	}, [finalAmout])

	return (
		<div className="relative">
			{isFilePending && (
				<div className="absolute inset-0 z-10 flex h-full w-full items-center justify-center bg-white bg-opacity-75">
					<Loader2 className="h-10 w-10 animate-spin text-primary" />
				</div>
			)}
			<Section
				title="Offline Payment"
				description="Use this option If you’ve already completed your payment through an offline method"
				fields={[
					<FormField
						key={"payment_mode"}
						control={form.control}
						name="payment_mode"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Payment Mode{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								{isSelectedOther ? (
									<div className="relative">
										<Input
											{...field}
											placeholder="Enter Payment Mode"
										/>
										<Button
											variant={"ghost"}
											className="absolute bottom-1/2 right-1 translate-y-1/2 cursor-pointer py-1"
											onClick={(e: React.MouseEvent) => {
												e.preventDefault()
												setIsSelectedOther(false)
												field.onChange(undefined)
											}}
										>
											<X />
										</Button>
									</div>
								) : (
									<Select
										value={field.value}
										disabled={field.disabled}
										onValueChange={value => {
											if (value == "OTHER") {
												setIsSelectedOther(true)
												field.onChange(undefined)
												form.setFocus("payment_mode")
											} else {
												setIsSelectedOther(false)
												field.onChange(value)
											}
											form.trigger(
												"transaction_reference",
											)
										}}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select Mode" />
										</SelectTrigger>
										<SelectContent>
											{paymentModeSchema.options?.map(
												(mode, index) => (
													<SelectItem
														key={index}
														value={mode}
													>
														{mode}
													</SelectItem>
												),
											)}
											<SelectItem
												key={"other"}
												value={"OTHER"}
											>
												OTHER
											</SelectItem>
										</SelectContent>
									</Select>
								)}

								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						key={"transaction_reference"}
						control={form.control}
						name="transaction_reference"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Transaction Reference / UTR ID{" "}
									{requiredReferenceStatuses.includes(
										form.watch("payment_mode"),
									) && (
										<span className="text-red-500">*</span>
									)}
								</FormLabel>
								<FormControl>
									<div className="relative">
										<Input
											{...field}
											placeholder="Enter UTR ID"
											className="border border-solid border-blue-400 pr-10"
										/>
										{isValidating && (
											<div className="absolute right-3 top-1/2 z-10 -translate-y-1/2">
												<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
											</div>
										)}
									</div>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						key={"payment_date"}
						control={form.control}
						name="payment_date"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Payment Date{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										type="date"
										{...field}
										placeholder="Select Date"
										max={
											new Date()
												.toISOString()
												.split("T")[0]
										}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						key={"account_holder_name"}
						control={form.control}
						name="account_holder_name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Sender Account Holder Name{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										type="text"
										{...field}
										placeholder="Enter Name of the holder"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						key={"bank_name"}
						control={form.control}
						name="bank_name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Sender Bank Name{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										type="text"
										{...field}
										placeholder="Bank Name"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						key={"amount_paid"}
						control={form.control}
						name="amount_paid"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Amount Paid{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										type="number"
										{...field}
										placeholder="Enter Amount Paid"
										onWheel={e => e.currentTarget.blur()}
										onKeyDown={e => {
											if (
												["e", "E", "+", "-"].includes(
													e.key,
												)
											) {
												e.preventDefault()
											}
										}}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						key={"proof_of_payment"}
						control={form.control}
						name="proof_of_payment"
						render={({ field: { onChange, value } }) => (
							<FormItem className="col-span-2 w-full">
								<FormLabel className="flex flex-col space-y-1">
									<div>
										Proof of Payment{" "}
										<span className="text-red-500">*</span>
										<span className="text-muted-foreground">
											(Max 5mb)
										</span>
									</div>
									<div className="text-xs italic">
										<span className="text-muted-foreground">
											In case of multiple payment proofs
											combine it to a single file
										</span>
									</div>
								</FormLabel>
								<FormControl>
									{value ? (
										<div className="flex w-full flex-col items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm md:w-1/2 md:flex-row">
											<div className="flex items-center space-x-3">
												{value.type.startsWith(
													"image/",
												) ? (
													<div className="relative h-12 w-full overflow-hidden rounded-md border border-gray-300 md:w-12">
														<img
															src={URL.createObjectURL(
																value,
															)}
															alt="Proof of Payment Preview"
															className="h-full w-full object-cover"
														/>
													</div>
												) : (
													<div className="flex h-12 w-full items-center justify-center rounded-md bg-blue-100 text-blue-600 md:w-12">
														<File className="h-6 w-6" />
													</div>
												)}
												<div className="flex flex-col">
													<span className="w-full truncate text-sm font-medium md:max-w-[200px]">
														{value.name}
													</span>
													<span className="text-xs text-muted-foreground">
														{(
															value.size /
															1024 /
															1024
														).toFixed(2)}{" "}
														MB
													</span>
												</div>
											</div>

											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={() =>
													onChange(undefined)
												}
												className="mt-2 text-red-500 hover:bg-red-50 md:mt-0"
											>
												<X className="mr-1 h-4 w-4" />
											</Button>
										</div>
									) : (
										<Dropzone
											onDrop={(acceptedFiles: File[]) => {
												onChange(acceptedFiles[0])
											}}
											dropZoneClassName="border-none bg-[#1D6AD30D]	 h-[20vh] "
											containerClassName="border border-dashed border-blue-500 rounded-md md:w-1/2 w-full"
											accept={{
												"image/jpeg": [".jpeg", ".jpg"],
												"image/png": [".png"],
												"application/pdf": [".pdf"],
											}}
											maxFiles={1}
											maxSize={5 * 1024 * 1024}
											onDropRejected={fileRejections => {
												fileRejections.forEach(
													rejection => {
														const { file, errors } =
															rejection
														errors.forEach(
															error => {
																if (
																	error.code ===
																	"file-too-large"
																) {
																	toast.error(
																		`${file.name} is too big. Max size is 5MB.`,
																	)
																} else if (
																	error.code ===
																	"file-invalid-type"
																) {
																	toast.error(
																		`File type not supported. Please upload JPEG, PNG, or PDF`,
																	)
																}
															},
														)
													},
												)
											}}
										>
											{(dropzone: DropzoneState) => (
												<>
													{dropzone.isDragAccept ? (
														<div className="text-sm font-medium">
															Drop your files
															here!
														</div>
													) : (
														<div className="flex flex-col items-center justify-center space-y-2">
															<div className="flex items-center justify-center space-x-3">
																<div>
																	<UploadCloud className="h-12 w-12 text-blue-500" />
																</div>
																<div className="flex flex-col items-center justify-center space-y-1">
																	<div className="flex items-center gap-0.5 font-medium">
																		Drop &
																		Drop
																		files or{" "}
																		<span className="text-blue-500 underline">
																			Browse
																		</span>
																	</div>
																	<div className="text-xs text-muted-foreground">
																		Supported
																		formats:
																		JPEG,
																		PNG, PDF
																	</div>
																</div>
															</div>
														</div>
													)}
												</>
											)}
										</Dropzone>
									)}
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						key={"remarks"}
						control={form.control}
						name="remarks"
						render={({ field }) => (
							<FormItem className="col-span-2">
								<FormLabel>Remarks</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										placeholder="Add Remarks"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
				]}
			/>
		</div>
	)
}

export default SelfOfflinePaymentForm
